import { ILang } from "./i18n/ILang";
import { IMembershipSubscriptionStatus, IUser } from "./user/IUser";

export const isBrowser = () => typeof window !== "undefined";

export const prefixedWithLang = (lang: ILang, url: string) => {
  function langInProd(lang: ILang): string {
    if (lang === ILang.sv) return "se";
    if (lang === ILang.es) return "es";
    return "";
  }
  return (lang === ILang.en ? `/${url}` : `/${langInProd(lang)}/${url}`).replace(/\/\//g, "/");
};

// "Weight-loss program" -> "weight-loss-program"
export const labelToDashes = (s: string) =>
  s
    .toLowerCase()
    .replace(/\s|[^\w]/g, "-")
    .replace(/^-|-$/, "");

// "/how-to-lose-weight/10-weeks/" -> "how-to-lose-weight-10-weeks"
export const hrefToDashes = (s: string) =>
  withoutParams(s).replace(/[^\w]/g, "-").replace(/^-|-$/, "");

// TODO: remove `withoutParams` before production
export const withoutParams = (s: string) => {
  // "http://www.example.com?a=true" -> "http://www.example.com"
  if (!isBrowser()) return s;

  const a = document.createElement("a");
  a.href = s;
  return a.pathname;
};

// true -> "open" — false -> "close"
export const mapBooleanToOpenClose = (bool: boolean) => (bool ? "open" : "close");

export const getInitial = (name: string) => name?.[0]?.toUpperCase();

export const getCurrentUrl = () => (isBrowser() ? document.location.href : "");

export const prefixFor = (url: string) => (url.indexOf("?") !== -1 ? "&" : "?");

export const hasRedirect = (url: string) => /redirect_to=/.test(url);

export const withRedirectTo = (redirectTo: string) => (url: string) =>
  !hasRedirect(redirectTo)
    ? `${url}${prefixFor(url)}redirect_to=${replaceWhenDDPro(redirectTo)}`
    : redirectTo;

export const replaceWhenDDPro = (url: string) => url.replace(/\/ddpro$/, "/pro");

export const getMobileDevice = (n: Navigator) => {
  const ua = n?.userAgent ?? "";
  const isAndroid = ua.search(/android/gi) !== -1;
  const isIPhone = ua.search(/iphone/gi) !== -1;
  const isIPad =
    ua.search(/ipad/gi) !== -1 || (n?.platform === "MacIntel" && n?.maxTouchPoints > 1);
  const isIOS = isIPhone || isIPad;

  return {
    isAndroid,
    isIOS,
    isIPad,
    isIPhone,
    isMobile: isAndroid || isIOS
  };
};

export function normalizeLang(defaultLang: ILang, lang: string): ILang {
  if (lang === "se") return ILang.sv;
  if (lang === "us") return ILang.en;
  if (lang !== ILang.en && lang !== ILang.es && lang !== ILang.sv) {
    return defaultLang;
  }
  return lang as ILang;
}

export const stopPropagation = (e: MouseEvent) => {
  const elType = (e.target as HTMLElement).nodeName;
  const isLinkOrButton = (type: string) => type === "BUTTON" || type === "A";

  if (!isLinkOrButton(elType)) {
    e.stopPropagation();
  }
};

export const isLoggedIn = (user: IUser) => typeof user !== "undefined";

export const isPremium = (user: IUser) =>
  isLoggedIn(user) && user.membershipSubscription?.status === IMembershipSubscriptionStatus.ACTIVE;

export const hasSubscription = (user: IUser) =>
  isLoggedIn(user) && user?.membershipSubscription?.status?.length > 0;

export const isPro = (user: IUser) => isLoggedIn(user) && typeof user.pro !== "undefined";

export const isDDProMemberPays = (user: IUser) =>
  isPro(user) && user.pro?.group?.subscriptionPayer === 2;

export const getGlobalThis = () => {
  // from https://github.com/zloirock/core-js/blob/master/packages/core-js/internals/global.js
  const check = it => it && it.Math == Math && it;

  return (
    check(typeof globalThis == "object" && globalThis) ||
    check(typeof window == "object" && window) ||
    check(typeof self == "object" && self) ||
    check(typeof global == "object" && global) ||
    (function () {
      return this;
    })() ||
    Function("return this")()
  );
};

export const getAPIEndPoint = (lang: ILang) => {
  switch (lang) {
    case ILang.en:
    return 'https://ddapi.prod.dietdoctor.com/v1'
    case ILang.sv: 
      return 'https://ddapi.prod.dietdoctor.com/se/v1'
    case ILang.es: 
      return 'https://ddapi.prod.dietdoctor.com/es/v1'
    default:
      return 'https://ddapi.prod.dietdoctor.com/v1'
  }
}
  
